import DeleteIcon from "@mui/icons-material/Delete"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro"
import { OrderStatusAutocomplete } from "components/OrderStatusAutocomplete/OrderStatusAutocomplete"
import { dataGridDEde } from "constants/dataGridLocale"
import {
  Address,
  ArticleItem,
  CustomerOrderCreateMutationVariables,
} from "generated/graphql"
import { enqueueSnackbar } from "notistack"
import { ArticleItemWithCount } from "pages/AricleSearchRoundAdd/ArticleSearchRoundAdd"
import {
  articleSearch,
  fetchArticleSearchFromOrder,
} from "queries/articleSearch"
import { customerOrderCreate } from "queries/customerOrderCreate"
import { useEffect, useRef, useState } from "react"
import { CustomerEditPageAddress } from "./CustomerEditPageAddress"

type AddOrderDialogProps = {
  customerID: string
  open: boolean
  close: () => void
  closeNewOrder: () => void
  previousOrderId?: number
}

export const AddOrderDialog = (props: AddOrderDialogProps) => {
  const { customerID, open, close, closeNewOrder, previousOrderId } = props
  const [selectedItems, setSelectedItems] = useState<ArticleItemWithCount[]>([])
  const searchRef = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [rows, setRows] = useState<ArticleItem[]>([])
  const [address, setAddress] = useState<Address | null>(null)
  const [status, setStatus] = useState<number>(0)
  const [zahlart, setZahlart] = useState<string>("")

  const handleOnClose = () => {
    setSelectedItems([])
    setRows([])
    setZahlart("")
    setStatus(0)
    setAddress(null)
    close()
  }

  const saveOrder = () => {
    const orderItems = selectedItems.map((item) => ({
      bdb: parseInt(item.bdb),
      anzahl: item.count,
      stueckpreis: Math.round(parseFloat(item.price) * 100),
    }))
    if (orderItems && zahlart && status && address) {
      const newOrder: CustomerOrderCreateMutationVariables = {
        in: {
          customerID: parseInt(customerID),
          status: status,
          zahlart: zahlart,
          deliveryAdrId: address.id,
          orderItems: orderItems,
          urOrderId: previousOrderId ? previousOrderId : 0,
        },
      }
      customerOrderCreate(newOrder)
        .then((res) => {
          if (res?.customerOrderCreate.orderID) {
            enqueueSnackbar(
              `Order ${res?.customerOrderCreate.orderID} angelegt`,
              {
                variant: "success",
              }
            )
          }
        })
        .finally(() => {
          setSelectedItems([])
          setRows([])
          setZahlart("")
          setStatus(0)
          setAddress(null)
          closeNewOrder()
        })
    }
  }

  useEffect(() => {
    if (
      previousOrderId !== undefined &&
      previousOrderId > 0 &&
      rows.length === 0 &&
      open === true
    ) {
      // search previous order articles
      fetchArticleSearchFromOrder(previousOrderId).then((result) => {
        if (result?.articleSearchFromOrder) {
          setRows(result.articleSearchFromOrder)
        }
      })
    }
  }, [previousOrderId, open, rows])

  const colArticle: GridColDef<ArticleItem>[] = [
    {
      field: "bdb",
      headerName: "BDB",
      width: 100,
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
            component="div"
            onClick={() =>
              window.open(
                `${window.location.origin}/editArticle/${params.row.bdb}`,
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            {params.row.bdb}
          </Typography>
        )
      },
    },
    {
      field: "pflNR",
      headerName: "PflNR",
      width: 100,
    },
    {
      field: "botname",
      headerName: "Artikel",
      renderCell: (params) => {
        return (
          <div>
            <div>{params.row.botname}</div>
            <div>
              {params.row.sizeFrom} - {params.row.sizeTo}
            </div>
          </div>
        )
      },
      width: 250,
    },
    {
      field: "soldStatus",
      headerName: "Verfügbarkeit",
      width: 150,
      renderCell: (params) => {
        return (
          <Typography
            sx={{
              fontSize: 9,
            }}
          >
            {params.value}
          </Typography>
        )
      },
    },
    {
      field: "add",
      headerName: "Hinzufügen",
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const item = selectedItems.find((i) => i.bdb === params.row.bdb)
              if (item === undefined) {
                setSelectedItems([
                  ...selectedItems,
                  { ...params.row, count: 1 },
                ])
              } else {
                item.count++
                setSelectedItems([...selectedItems])
              }
            }}
          >
            Hinzufügen
          </Button>
        )
      },
      width: 120,
    },
  ]

  const colArticleItemWithCount: GridColDef<ArticleItemWithCount>[] = [
    {
      field: "bdb",
      headerName: "BDB",
      width: 80,
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
            component="div"
            onClick={() =>
              window.open(
                `${window.location.origin}/editArticle/${params.row.bdb}`,
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            {params.row.bdb}
          </Typography>
        )
      },
    },
    {
      field: "botname",
      headerName: "Artikel",
      renderCell: (params) => {
        return (
          <div>
            <div>{params.row.botname}</div>
            <div>
              {params.row.sizeFrom} - {params.row.sizeTo}
            </div>
          </div>
        )
      },
      width: 300,
    },
    {
      field: "price",
      headerName: "Stückpreis",
      width: 100,
      type: "number",
      editable: true,
    },

    {
      field: "count",
      headerName: "Anzahl",
      width: 100,
      type: "number",
      editable: true,
    },
    {
      field: "remove",
      headerName: "Entfernen",
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => {
              const item = selectedItems.find((i) => i.bdb === params.row.bdb)
              if (item !== undefined) {
                setSelectedItems(
                  selectedItems.filter((i) => i.bdb !== item.bdb)
                )
              }
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        )
      },
    },
  ]

  useEffect(() => {
    if (searchRef.current !== null) {
      searchRef.current.focus()
    }
  }, [])

  const handleSearchCommit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const searchValue = searchRef.current?.value
      if (searchValue !== undefined && searchValue !== null) {
        if (searchValue === "") {
          enqueueSnackbar("Bitte geben Sie einen Suchbegriff ein", {
            variant: "info",
          })
        } else {
          setIsLoading(true)
          articleSearch(searchValue, false)
            .then((result) => {
              if (result?.articleSearch) {
                setRows(result.articleSearch)
              }
            })
            .finally(() => {
              setIsLoading(false)
              setTimeout(() => {
                if (searchRef.current !== null) {
                  searchRef.current.focus()
                }
              }, 100)
            })
        }
      }
    }
  }

  return (
    <Dialog open={open} onClose={handleOnClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {previousOrderId
          ? `Nachlieferung zu ${previousOrderId}`
          : "Bestellung hinzufügen"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="h6" component="div">
              Stammdaten
            </Typography>
          </Grid>
          <Grid size={6}>
            <OrderStatusAutocomplete
              status={status}
              handleOrderStatusChange={(newStatus) => {
                setStatus(newStatus)
              }}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel id="zahlart">Zahlart</InputLabel>
              <Select
                disabled={false}
                labelId="zahlart"
                id="zahlart"
                value={zahlart}
                label="Zahlart"
                onChange={(e) => setZahlart(e.target.value)}
              >
                <MenuItem value={"Vorkasse"}>Vorkasse</MenuItem>
                <MenuItem value={"Rechnung"}>Rechnung</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <CustomerEditPageAddress
              customerID={customerID}
              setSelectedAddress={setAddress}
              open={false}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="h6" component="div">
              Artikel hinzufügen
            </Typography>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextField
                  label="Suche"
                  inputRef={searchRef}
                  placeholder="Suche"
                  onKeyUp={handleSearchCommit}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={6}>
                <Box sx={{ height: "50vh", width: "100%" }}>
                  <DataGridPro
                    rows={rows}
                    getRowId={(row) => row.bdb}
                    columns={colArticle}
                    localeText={dataGridDEde}
                    loading={isLoading}
                  />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box sx={{ height: "50vh", width: "100%" }}>
                  <DataGridPro
                    rows={selectedItems}
                    getRowId={(row) => row.bdb + row.pflNR}
                    columns={colArticleItemWithCount}
                    localeText={dataGridDEde}
                    loading={isLoading}
                    processRowUpdate={(newRow, oldRow) => {
                      if (
                        newRow.count === oldRow.count &&
                        newRow.price === oldRow.price
                      ) {
                        return newRow
                      }
                      if (newRow.count <= 0) {
                        enqueueSnackbar(
                          "Anzahl darf nicht kleiner als 1 sein",
                          {
                            variant: "error",
                          }
                        )
                        return oldRow
                      }
                      const item = selectedItems.find(
                        (i) => i.bdb === newRow.bdb
                      )
                      if (item !== undefined) {
                        item.count = Number(newRow.count)
                        item.price = newRow.price
                        setSelectedItems([...selectedItems])
                      }
                      return newRow
                    }}
                    onProcessRowUpdateError={(error) => {
                      enqueueSnackbar(error, { variant: "error" })
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="error"
          fullWidth
          variant="contained"
          onClick={handleOnClose}
        >
          Abbrechen
        </Button>

        <Button
          variant="contained"
          fullWidth
          disabled={
            selectedItems.length === 0 ||
            zahlart === "" ||
            status <= 0 ||
            address === null
          }
          onClick={saveOrder}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  )
}
